import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReportSecurityNote } from '../../components/ReportSecurityNote';
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, CheckCircle2, Circle, XCircle, Download, RefreshCw, Rocket, AlertTriangle, Presentation, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { ciApi } from "../../components/ci/ciApi";
import { Panel, Kicker } from "../../components/ci/ciWidgets";
import { BrandingDialog } from "../../components/ci/BrandingDialog";
import { OverviewTab } from "../../components/ci/workspace/OverviewTab";
import { ScorecardTab } from "../../components/ci/workspace/ScorecardTab";
import { DimensionsTab } from "../../components/ci/workspace/DimensionsTab";
import { CompetitionTab } from "../../components/ci/workspace/CompetitionTab";
import { QuadrantTab } from "../../components/ci/workspace/QuadrantTab";
import { TrendsTab } from "../../components/ci/workspace/TrendsTab";
import { RisksTab } from "../../components/ci/workspace/RisksTab";
import { ValuePoolsTab } from "../../components/ci/workspace/ValuePoolsTab";
import { RoadmapTab } from "../../components/ci/workspace/RoadmapTab";
import { EvidenceTab } from "../../components/ci/workspace/EvidenceTab";

const TABS = [
  ["overview", "Overview", OverviewTab],
  ["scorecard", "Scorecard", ScorecardTab],
  ["dimensions", "Dimensions", DimensionsTab],
  ["competition", "Competition", CompetitionTab],
  ["quadrant", "Leadership Quadrant", QuadrantTab],
  ["trends", "Industry Trends", TrendsTab],
  ["risks", "Risks", RisksTab],
  ["valuepools", "Value Pools", ValuePoolsTab],
  ["roadmap", "Roadmap", RoadmapTab],
  ["evidence", "Evidence & Gaps", EvidenceTab],
];

const ReportMetaBar = ({ report }) => {
  const gov = report.governance || {};
  const generated = (gov.generated_at || report.generated_at || "").slice(0, 10);
  return (
    <div
      data-testid="ci-governance-bar"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-emerald-400/25 bg-emerald-400/[0.04] px-5 py-3 mb-6"
    >
      <ShieldCheck className="w-4 h-4 text-emerald-300 shrink-0" />
      <span className="text-[12px] font-bold text-emerald-300" data-testid="ci-review-status">
        {report.quality_review?.status === 'issues_detected' ? 'ISSUED — report discrepancies require attention' : 'FINAL — issued by the SAMANVAI Intelligence Engine'}
      </span>
      <span className="text-[11px] text-white/40">
        {gov.document_id} · v{gov.version || report.version || 1}
        {generated ? ` · generated ${generated}` : ""}
      </span>
      <span className="ml-auto text-[11px] text-white/35 italic">
        Residual data gaps are consolidated in the Evidence &amp; Gaps annexure
      </span>
    </div>
  );
};

const AgentRow = ({ agent }) => {
  const icons = {
    pending: <Circle className="w-4 h-4 text-white/25" />,
    running: <Loader2 className="w-4 h-4 text-teal-300 animate-spin" />,
    complete: <CheckCircle2 className="w-4 h-4 text-emerald-300" />,
    error: <XCircle className="w-4 h-4 text-red-300" />,
  };
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors ${
        agent.status === "running" ? "border-teal-400/35 bg-teal-400/[0.05]" : agent.status === "complete" ? "border-white/10 bg-white/[0.02]" : "border-white/8"
      }`}
      data-testid={`ci-agent-${agent.key}`}
    >
      {icons[agent.status] || icons.pending}
      <span className={`text-[13px] font-medium flex-1 ${agent.status === "pending" ? "text-white/40" : "text-white/85"}`}>
        {agent.name}
      </span>
      {agent.detail && <span className="text-[11px] text-white/45 truncate max-w-[280px]">{agent.detail}</span>}
    </div>
  );
};

export default function CIWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState("overview");
  const [downloading, setDownloading] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await ciApi.get(`/assessments/${id}`);
      setAssessment(r.data);
      if (r.data.status === "complete") {
        const rep = await ciApi.get(`/assessments/${id}/report`);
        setReport(rep.data);
      }
      return r.data.status;
    } catch {
      toast.error("Failed to load the assessment");
      return null;
    }
  }, [id]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (assessment?.status === "analyzing") {
      pollRef.current = setInterval(async () => {
        const status = await load();
        if (status !== "analyzing") clearInterval(pollRef.current);
      }, 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [assessment?.status, load]);

  const runAnalysis = async () => {
    try {
      await ciApi.post(`/assessments/${id}/analyze`);
      toast.success("Multi-agent analysis launched");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to launch");
    }
  };

  const reloadReport = useCallback(async () => {
    const rep = await ciApi.get(`/assessments/${id}/report`);
    setReport(rep.data);
  }, [id]);

  const downloadFile = async (kind) => {
    setDownloading(kind);
    try {
      const r = await ciApi.get(`/assessments/${id}/report/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const base = (assessment?.company?.name || "report").replace(/ /g, "_");
      a.download = kind === "pdf" ? `SAMANVAI_Company_Intelligence_${base}.pdf` : `SAMANVAI_Board_Deck_${base}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`${kind.toUpperCase()} export failed`);
    } finally {
      setDownloading(null);
    }
  };

  if (!assessment)
    return (
      <div className="flex justify-center py-24 text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  const c = assessment.company || {};
  const agents = assessment.progress?.agents || [];
  const done = agents.filter((a) => a.status === "complete").length;

  return (
    <div data-testid="ci-workspace-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Kicker className="mb-3">
              Company workspace · {c.industry || "—"} {c.sub_sector ? `· ${c.sub_sector}` : ""}
            </Kicker>
            <h1 className="font-serif text-3xl sm:text-4xl" data-testid="ci-workspace-company-name">{c.name}</h1>
            <div className="text-[12px] text-white/45 mt-2">
              {assessment.purpose} · {c.reporting_period || "Latest period"} · {c.reporting_currency}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <BrandingDialog assessmentId={id} />
            {assessment.status === "complete" && (
              <>
                <button
                  onClick={() => downloadFile("pdf")}
                  disabled={!!downloading}
                  data-testid="ci-export-pdf-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#231a03] font-bold text-sm px-6 py-2.5 transition-colors"
                >
                  {downloading === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Board pack (PDF)
                </button>
                <button
                  onClick={() => downloadFile("pptx")}
                  disabled={!!downloading}
                  data-testid="ci-export-pptx-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-teal-400/50 text-teal-300 hover:bg-teal-400/10 disabled:opacity-40 font-bold text-sm px-6 py-2.5 transition-colors"
                >
                  {downloading === "pptx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Presentation className="w-4 h-4" />}
                  Board deck (PPTX)
                </button>
              </>
            )}
          </div>
        </div>

        {/* -------- analyzing state -------- */}
        {assessment.status === "analyzing" && (
          <Panel className="p-8 max-w-3xl mx-auto" data-testid="ci-analysis-progress">
            <div className="text-center mb-8">
              <Loader2 className="w-8 h-8 text-teal-300 animate-spin mx-auto mb-4" />
              <h2 className="font-serif text-2xl mb-2">The {agents.length || 14}-agent analysis is under way</h2>
              <p className="text-[13px] text-white/50">
                {done} of {agents.length} agents complete · typically 4–8 minutes · you can leave this page and return
              </p>
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden max-w-md mx-auto mt-4">
                <div className="h-full bg-teal-400 rounded-full transition-all duration-700" style={{ width: `${(done / Math.max(agents.length, 1)) * 100}%` }} />
              </div>
            </div>
            <div className="space-y-2">
              {agents.map((a) => (
                <AgentRow key={a.key} agent={a} />
              ))}
            </div>
          </Panel>
        )}

        {/* -------- draft state -------- */}
        {assessment.status === "draft" && (
          <Panel className="p-10 max-w-2xl mx-auto text-center" data-testid="ci-draft-state">
            <Rocket className="w-8 h-8 text-teal-300 mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-3">This assessment has not been analysed yet</h2>
            <p className="text-[13px] text-white/55 mb-2">
              {assessment.sources?.length || 0} source document(s) uploaded · {(assessment.peers || []).length} peers{" "}
              {assessment.peers_approved ? "approved" : "pending approval"}
            </p>
            <p className="text-[12px] text-white/40 mb-8">
              You can launch the multi-agent analysis now, or return to the wizard to add sources and peers first.
            </p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate("/company-intelligence/new")}
                className="rounded-full border border-white/20 text-white/70 hover:text-white text-sm font-semibold px-6 py-2.5 transition-colors"
              >
                Back to setup
              </button>
              <button
                onClick={runAnalysis}
                data-testid="ci-run-analysis-btn"
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-[#231a03] font-bold text-sm px-7 py-2.5 transition-colors"
              >
                <Rocket className="w-4 h-4" /> Launch analysis
              </button>
            </div>
          </Panel>
        )}

        {/* -------- error state -------- */}
        {assessment.status === "error" && (
          <Panel className="p-10 max-w-2xl mx-auto text-center border-red-400/25" data-testid="ci-error-state">
            <AlertTriangle className="w-8 h-8 text-red-300 mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-3">The analysis hit an error</h2>
            <p className="text-[13px] text-white/55 mb-6 break-words">{assessment.progress?.error}</p>
            <button
              onClick={runAnalysis}
              data-testid="ci-retry-analysis-btn"
              className="inline-flex items-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 text-[#06251f] font-bold text-sm px-7 py-2.5 transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Retry the analysis
            </button>
          </Panel>
        )}

        {/* -------- complete: tabs -------- */}
        {assessment.status === "complete" && report && (
          <>
            <ReportMetaBar report={report} />
            <div className="flex items-center gap-1 overflow-x-auto border-b border-white/10 mb-8 -mx-1 px-1" data-testid="ci-workspace-tabs">
              {TABS.map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  data-testid={`ci-tab-btn-${key}`}
                  className={`px-4 py-3 text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                    tab === key ? "border-teal-400 text-teal-300" : "border-transparent text-white/45 hover:text-white/80"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {TABS.map(([key, , Component]) => (tab === key ? <Component key={key} report={report} onReload={reloadReport} /> : null))}
            <ReportSecurityNote dark />
          </>
        )}
      </div>
  );
}
